import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';

import axios from './api';
import { ENDPOINTS } from '../endpoints';
import SuspenseLoader from './components/SuspenseLoader';
import { trackPageView } from './utils/analytics';
import CookieBanner from './components/CookieBanner';

const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Contact = lazy(() => import('./pages/Contact'));
const CompanyHome = lazy(() => import('./pages/CompanyHome'));
const ModelHome = lazy(() => import('./pages/ModelHome'));
const AdminHome = lazy(() => import('./pages/AdminHome'));
const AdminCron = lazy(() => import('./pages/AdminCron'));
const AdminNotifications = lazy(() => import('./pages/AdminNotifications'));
const AdminUserManager = lazy(() => import('./pages/AdminUserManager'));
const AdminReports = lazy(() => import('./pages/AdminReports'));
const Home = lazy(() => import('./pages/Home'));
const Following = lazy(() => import('./pages/Following'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Profile = lazy(() => import('./pages/Profile'));
const NotFound = lazy(() => import('./pages/NotFound'));
const ServerError = lazy(() => import('./pages/ServerError'));
const Activate = lazy(() => import('./pages/Activate'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Followers = lazy(() => import('./pages/Followers'));
const Inbox = lazy(() => import('./pages/Inbox'));
const InfluencerGrid = lazy(() => import('./pages/InfluencerGrid'));
const MyGrid = lazy(() => import('./pages/MyGrid'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const PortfolioPublic = lazy(() => import('./pages/PortfolioPublic'));

function App(){
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if(!token){
        setUser(null);
        setAuthChecked(true);
        return;
      }
      try {
        const response = await axios.get(ENDPOINTS.auth.me);
        setUser(response.data);
      } catch {
        setUser(null);
      } finally {
        setAuthChecked(true);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);

  useEffect(() => {
    // ensure we always start at top when changing routes
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if(!authChecked) return;

    if(user?.suspended){
      const allowed = ['/notifications', '/inbox'];
      const isAllowed = allowed.some((path) => location.pathname.startsWith(path));
      if(!isAllowed){
        navigate('/notifications', { replace: true });
      }
      return;
    }

    if(user){
      const target = user.is_admin ? '/admin' : user.is_company ? '/company' : user.is_influencer ? '/model' : '/';
      const params = new URLSearchParams(location.search || '');
      const wantsPublicHome = params.get('public') === '1';
      if(location.pathname === '/' && target !== '/' && !wantsPublicHome){
        navigate(target, { replace: true });
      }
    } else {
      const protectedPaths = ['/admin', '/admin/cron', '/admin/notifications', '/admin/users', '/admin/reports', '/company', '/model', '/projects', '/portfolio', '/my-grid', '/following', '/followers', '/profile', '/notifications', '/inbox'];
      if(protectedPaths.some(p => location.pathname.startsWith(p))){
        navigate('/', { replace: true });
      }
    }
  }, [user, authChecked, location.pathname, location.search, navigate]);

  useEffect(() => {
    const handleSuspended = () => {
      setUser((prev) => (prev ? { ...prev, suspended: true } : prev));
    };
    window.addEventListener('user:suspended', handleSuspended);
    return () => window.removeEventListener('user:suspended', handleSuspended);
  }, []);

  useEffect(() => {
    // avoid browser restoring previous scroll on navigation (so our scrollTo takes effect)
    if ('scrollRestoration' in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      return () => { window.history.scrollRestoration = prev; };
    }
  }, []);

  if(!authChecked) return null;

  return (
    <Suspense fallback={<SuspenseLoader /> }>
      <CookieBanner />
      <Routes>
        <Route path="/login" element={<Login onLogin={setUser} />} />
        <Route path="/register" element={<Register onRegister={setUser} />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/company" element={<CompanyHome user={user} onUserChange={setUser} />} />
        <Route path="/model" element={<ModelHome user={user} onUserChange={setUser} />} />
        <Route path="/my-grid" element={<MyGrid user={user} onUserChange={setUser} />} />
        <Route path="/portfolio" element={<Portfolio user={user} onUserChange={setUser} />} />
        <Route path="/p/:id" element={<PortfolioPublic user={user} onUserChange={setUser} />} />
        <Route path="/admin" element={<AdminHome user={user} onUserChange={setUser} />} />
        <Route path="/admin/cron" element={<AdminCron user={user} onUserChange={setUser} />} />
        <Route path="/admin/notifications" element={<AdminNotifications user={user} onUserChange={setUser} />} />
        <Route path="/admin/users" element={<AdminUserManager user={user} onUserChange={setUser} />} />
        <Route path="/admin/reports" element={<AdminReports user={user} onUserChange={setUser} />} />
        <Route path="/following" element={<Following user={user} onUserChange={setUser} />} />
        <Route path="/followers" element={<Followers user={user} onUserChange={setUser} />} />
        <Route path="/notifications" element={<Notifications user={user} onUserChange={setUser} />} />
        <Route path="/inbox" element={<Inbox user={user} onUserChange={setUser} />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/activate" element={<Activate onActivate={setUser} />} />
        <Route path="/profile" element={<Profile user={user} onUserChange={setUser} />} />
        <Route path="/influencer/:id/grid" element={<InfluencerGrid user={user} onUserChange={setUser} />} />
        <Route path="/" element={<Home user={user} onUserChange={setUser} />} />
        <Route path="/500" element={<ServerError />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default App;
